/**
 * ProjectUnlocksPage — Issue #134
 *
 * 项目解锁状态管理页面：
 *   - 列表展示（按 orderProjectId / status 过滤）
 *   - 老板级特批解锁（special_override）
 *
 * 未解锁项目不得排期、不得创建服务工单、不得创建实验室制备任务。
 * 特殊解绑必须老板级审批。
 * approvedBy 由后端从 JWT ctx.userId 派生，前端不传。
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import Decimal from 'decimal.js';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useProjectUnlocks,
  type ProjectUnlockResponseDto,
  type SpecialOverrideUnlockDto,
} from '@/hooks/useFinance';

/** Format a ratio (0-1) as percentage string using Decimal. */
function fmtPct(ratio: string): string {
  return `${new Decimal(ratio).mul(100).toFixed(1)}%`;
}

const STATUS_LABELS: Record<string, string> = {
  locked: '已锁定',
  unlocked: '已解锁',
  expired: '已过期',
};

const UNLOCK_TYPE_LABELS: Record<string, string> = {
  auto_ratio: '付款比例自动解锁',
  special_override: '老板特批解锁',
};

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    locked: 'bg-red-100 text-red-700',
    unlocked: 'bg-green-100 text-green-800',
    expired: 'bg-gray-100 text-gray-500',
  };
  const cls = colorMap[status] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Special Override Dialog
// approvedBy is NOT in the form — it is derived from ctx.userId on the backend.
// ─────────────────────────────────────────────────────────────────────────────

interface SpecialOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  onApproved: () => void;
}

function SpecialOverrideDialog({ open, onClose, onApproved }: SpecialOverrideDialogProps) {
  const { specialOverride, loading, error } = useProjectUnlocks();
  // SpecialOverrideUnlockDto no longer has approvedBy (removed in P1-2 fix)
  const [form, setForm] = useState<Omit<SpecialOverrideUnlockDto, 'approvedBy'>>({
    orderProjectId: '',
    specialApprovalId: '',
  });

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = await specialOverride(form as SpecialOverrideUnlockDto);
    if (result) {
      onApproved();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold">老板特批解锁</h2>
        <p className="mb-4 text-sm text-gray-500">
          特殊解绑必须老板级审批。审批人由系统自动记录为当前登录用户。
        </p>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error.message}</div>
        )}
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">订单项目 ID</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.orderProjectId}
              onChange={(e) => setForm((f) => ({ ...f, orderProjectId: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">审批记录 ID</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.specialApprovalId}
              onChange={(e) => setForm((f) => ({ ...f, specialApprovalId: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">解锁到期时间（可选）</label>
            <input
              type="datetime-local"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100">取消</button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {loading ? '提交中…' : '确认特批解锁'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function ProjectUnlocksPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, FINANCE_PERMISSIONS.PROJECT_UNLOCK_READ);
  const canSpecialOverride = hasPermission(permissions, FINANCE_PERMISSIONS.PROJECT_UNLOCK_SPECIAL_OVERRIDE);

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有查看项目解锁状态的权限。
      </div>
    );
  }

  const { unlocks, loading, error, fetchUnlocks } = useProjectUnlocks();
  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [specialDialogKey, setSpecialDialogKey] = useState(0);
  const [showSpecialDialog, setShowSpecialDialog] = useState(false);

  useEffect(() => {
    void fetchUnlocks({
      orderProjectId: filterProjectId || undefined,
      status: filterStatus || undefined,
    });
  }, [fetchUnlocks, filterProjectId, filterStatus]);

  function openSpecialDialog() {
    setSpecialDialogKey((k) => k + 1);
    setShowSpecialDialog(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">项目解锁状态</h1>
        {canSpecialOverride && (
          <button
            onClick={openSpecialDialog}
            className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
          >
            老板特批解锁
          </button>
        )}
      </div>

      <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
        <strong>注意：</strong>未解锁项目不得排期、不得创建服务工单、不得创建实验室制备任务。
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <input
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          placeholder="订单项目 ID 过滤"
          value={filterProjectId}
          onChange={(e) => setFilterProjectId(e.target.value)}
        />
        <select
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">加载中…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">项目 ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">解锁类型</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">要求比例</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">已付比例</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">审批人</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">到期时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {unlocks.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">暂无记录</td>
                </tr>
              ) : (
                unlocks.map((u: ProjectUnlockResponseDto) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{u.orderProjectId.slice(0, 8)}…</td>
                    <td className="px-4 py-3">{UNLOCK_TYPE_LABELS[u.unlockType] ?? u.unlockType}</td>
                    <td className="px-4 py-3">{fmtPct(u.unlockRatio)}</td>
                    <td className="px-4 py-3">
                      <span className={new Decimal(u.paidRatio).gte(new Decimal(u.unlockRatio)) ? 'font-semibold text-green-700' : 'text-red-600'}>
                        {fmtPct(u.paidRatio)}
                      </span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                    <td className="px-4 py-3 text-gray-500">{u.approvedBy ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {u.expiresAt ? new Date(u.expiresAt).toLocaleDateString('zh-CN') : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <SpecialOverrideDialog
        key={specialDialogKey}
        open={showSpecialDialog}
        onClose={() => setShowSpecialDialog(false)}
        onApproved={() => {
          void fetchUnlocks({ orderProjectId: filterProjectId || undefined, status: filterStatus || undefined });
        }}
      />
    </div>
  );
}
